/**
 * SAIGA VIEWMODEL — full first-person weapon replacement.
 *
 * Loads fps_saiga_animations.glb (gun + hands + baked clips, authored at metre
 * scale with the eye at Head_Cam_014 = (0, 0.473, 0)) and drives its AnimationMixer
 * from the game's state vector. The baked clips own all the motion — no procedural
 * sway/bob/recoil stack on top, that rig is hidden when this mounts.
 *
 * Clip mapping (state -> GLB clip):
 *   idle        -> Armature|Saiga_Idle
 *   walk/sprint -> Armature|Saiga_Walk
 *   fire        -> Armature|Saiga_Fire      (restarted per shot from addRecoil)
 *   reloadTac   -> Armature|Saiga_Reload_Fast
 *   reloadEmpty -> Armature|Saiga_Reload_Full
 *
 * Reload completion is synthesised from clip fractions (the baked clips carry no
 * event markers): WeaponSystem completes the mag via `magin` and settles via `end`,
 * so we emit those at the clip positions where the mag visibly re-enters.
 *
 * The procedural Viewmodel stays fully alive underneath (weapon groups, arm rig,
 * ballistics muzzle) but its `rig` is made invisible — the game's ammo, spread,
 * tracer and shell logic then keep working unchanged; only the pixels change.
 *
 * Mounts as a sibling of the procedural rig under the viewmodel anchor, so it
 * inherits the per-frame camera copy (lighting + shadow agreement) for free.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import saigaGltfUrl from '../../assets/models/fps_saiga_animations.glb?url';

// Reload event fractions (0..1) of the reload clip duration.
const RELOAD_EVENTS = [
  { t: 0.0, name: 'start' },
  { t: 0.22, name: 'magdrop' },
  { t: 0.55, name: 'magin' },
  { t: 0.82, name: 'boltrelease' },
  { t: 1.0, name: 'end' },
];

export class SaigaViewmodel {
  /**
   * @param {object} anchor  the viewmodel anchor (camera-relative parent)
   */
  constructor(anchor) {
    this.group = null;
    this.mixer = null;
    this.clips = {}; // key -> AnimationClip
    this.action = null; // active mixer action
    this.emit = null; // (name, clipName) => void  (wired to viewmodel.onClipEvent)

    // Contract surface mirrored for WeaponSystem / HUD reads.
    this.clip = null; // { name: 'reloadTac'|..., duration }
    this.clipT = 0;
    this.reloadArmed = false;

    this._anchor = anchor;
    this._muzzleObj = null;
    this._muzzleBox = null;
    this.bore = new THREE.Vector3(0, 0, -1);
    this._prevReloadFrac = 0;
    this._inReload = false;
    this._fireClip = null;
    this._fireUntil = 0;
    this._lastTrigger = false;
    this._lastSpeed = 0;
    this._elapsed = 0;
  }

  /** @returns {Promise<void>} resolves once mounted and idling. */
  async mount() {
    const loader = new GLTFLoader();
    const url = saigaGltfUrl ?? '/assets/models/fps_saiga_animations.glb';

    const loadWithTimeout = (targetUrl, ms = 25000) => {
      return Promise.race([
        loader.loadAsync(targetUrl),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout loading Saiga GLB (${targetUrl}) after ${ms}ms`)), ms)
        ),
      ]);
    };

    let gltf;
    try {
      gltf = await loadWithTimeout(url);
    } catch (err) {
      console.warn('[saiga] Primary load failed/timed out, retrying...', err);
      gltf = await loadWithTimeout(url, 35000);
    }

    const group = gltf.scene;
    // Muzzle faces +Z in the file; rotate so it points away from the eye (-Z).
    group.rotation.y = Math.PI;
    // The eye is 0.473 m above the model root; drop the model so the eye lands
    // exactly on the anchor origin (the camera).
    group.position.y = -0.473;

    // Ensure opaque rendering for all weapon and arm materials to prevent background bleed.
    // Three.js MeshStandardMaterial outputs diffuseColor.a (which includes map.a) to the render target's alpha
    // channel even if transparent=false. Since the viewmodel composite shader relies on the RT's alpha channel
    // for coverage (world * (1 - alpha) + v.rgb), any map with non-1.0 alpha will cause background bleed.
    group.traverse((o) => {
      if (o.isMesh && o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of mats) {
          m.transparent = false;
          m.depthWrite = true;
          m.opacity = 1.0;
          const prevCompile = m.onBeforeCompile;
          m.onBeforeCompile = (shader, renderer) => {
            if (prevCompile) prevCompile(shader, renderer);
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <dithering_fragment>',
              '#include <dithering_fragment>\n  gl_FragColor.a = 1.0;'
            );
          };
          m.needsUpdate = true;
        }
      }
    });

    this._anchor.add(group);
    this.group = group;

    this.mixer = new THREE.AnimationMixer(group);
    for (const a of gltf.animations) {
      const n = a.name;
      const key = n.includes('Idle')
        ? 'idle'
        : n.includes('Walk')
          ? 'walk'
          : n.includes('Fire')
            ? 'fire'
            : n.includes('Reload_Fast')
              ? 'reloadFast'
              : n.includes('Reload_Full')
                ? 'reloadFull'
                : n;
      this.clips[key] = a;
    }
    // A muzzle reference for ballistics: the mesh whose bbox reaches farthest -Z
    // (the barrel tip after the 180° rotation).
    group.updateMatrixWorld(true);
    let bestZ = Infinity;
    let best = null;
    group.traverse((o) => {
      if (!o.isMesh) return;
      const bb = new THREE.Box3().setFromObject(o);
      if (bb.min.z < bestZ) {
        bestZ = bb.min.z;
        best = o;
      }
    });
    this._muzzleObj = best;
    this._fireClip = this.clips.fire ?? null;
    this._play('idle');
    return this;
  }

  /** Start a named clip. `loop=false` runs once and stays on the last frame. */
  _play(key, loop = true) {
    const a = this.clips[key];
    if (!a) return 0;
    if (this.action?.getClip() === a && this.action.isRunning()) return a.duration;
    const action = this.mixer.clipAction(a);
    action.reset();
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce);
    if (!loop) action.clampWhenFinished = true;
    action.play();
    this.action = action;
    return a.duration;
  }

  /** WeaponSystem.viewmodel.play(name) — returns duration (0 = not handled). */
  play(name) {
    switch (name) {
      case 'reloadTac':
        this._inReload = true;
        this._prevReloadFrac = 0;
        this.reloadArmed = true;
        this.clip = { name: 'reloadTac', duration: this._play('reloadFast', false) };
        this.clipT = 0;
        return this.clip.duration;
      case 'reloadEmpty':
        this._inReload = true;
        this._prevReloadFrac = 0;
        this.reloadArmed = true;
        this.clip = { name: 'reloadEmpty', duration: this._play('reloadFull', false) };
        this.clipT = 0;
        return this.clip.duration;
      case 'draw':
      case 'inspect':
        this._play('idle');
        return 0;
      case 'holster':
        // No baked holster clip; snap back to idle and report instantly done.
        this._play('idle');
        return 0.01;
      default:
        return 0;
    }
  }

  stopClip() {
    if (this._inReload) {
      this._inReload = false;
      this.reloadArmed = false;
      this.clip = null;
      this.clipT = 0;
      this._play('idle');
    }
  }

  get clipPlaying() {
    return this._inReload;
  }

  get clipName() {
    return this._inReload ? this.clip?.name ?? null : null;
  }

  /** Per-shot viewmodel kick. The baked Fire clip owns the recoil look. */
  addRecoil() {
    if (!this._fireClip) return;
    this._play('fire', false);
    // Hold the fire clip through the shot cadence, then fall back to idle/walk.
    this._fireUntil = this._elapsed + 0.28;
  }

  jump() {}
  land() {}

  /**
   * Drive the mixer from the game state vector. Call once per frame with the same
   * `s` the procedural viewmodel receives ({ ads, sprint, speed, trigger, ... }).
   */
  update(dt, s) {
    if (!this.mixer || !this.group) return;
    this._elapsed += dt;
    this.mixer.update(dt);

    // Reload plays to completion; emit synthesised phase events once.
    if (this._inReload && this.clip) {
      this.clipT = Math.min(this.clipT + dt, this.clip.duration);
      const frac = this.clip.duration > 0 ? this.clipT / this.clip.duration : 1;
      for (const ev of RELOAD_EVENTS) {
        if (ev.t > this._prevReloadFrac && ev.t <= frac) this.emit?.(ev.name, this.clip.name);
      }
      this._prevReloadFrac = frac;
      if (this.clipT >= this.clip.duration) {
        this._inReload = false;
        this.reloadArmed = false;
        this.clip = null;
        this.clipT = 0;
        this._play('idle');
      }
      return;
    }

    // Fire clip outranks locomotion; once exhausted return to idle/walk.
    if (this._elapsed < this._fireUntil) return;

    const moving = (s?.speed ?? 0) > 0.4;
    const sprint = s?.sprint === true;
    const wantWalk = moving || sprint;
    if (wantWalk !== this._lastSpeed) {
      this._lastSpeed = wantWalk;
      this._play(wantWalk ? 'walk' : 'idle');
    }
  }

  dispose() {
    this.group?.removeFromParent();
    this.group = null;
    this.mixer = null;
  }

  /* ====================================================================== */
  /*  world-space queries for firing                                        */
  /* ====================================================================== */

  /** Muzzle in world space (viewScene == world because anchor tracks camera). */
  muzzleWorld(out) {
    if (!this._muzzleObj || !this.group) return out.set(0, 0, 0);
    this.group.updateMatrixWorld(true);
    // The barrel mesh is skinned; its local bbox is in bind pose, which is close
    // enough to the baked pose for a spawn origin. Use the bbox tip.
    const bb = this._muzzleBox ?? (this._muzzleBox = new THREE.Box3());
    bb.setFromObject(this._muzzleObj);
    bb.getCenter(out);
    return out;
  }

  /** Ejection port: offset the muzzle back along the bore (port sits behind it). */
  ejectWorld(out) {
    this.muzzleWorld(out);
    if (this.group) out.addScaledVector(this.bore, -0.12);
    return out;
  }

  ejectVelocity(out, speed = 2.6) {
    this.boreDir(out).multiplyScalar(-speed * 0.4);
    out.y -= speed * 0.35;
    out.x += 0.3;
    return out;
  }

  boreDir(out) {
    if (this.group) {
      this.group.updateMatrixWorld(true);
      this.bore.set(0, 0, -1).transformDirection(this.group.matrixWorld);
    } else {
      this.bore.set(0, 0, -1);
    }
    return out.copy(this.bore);
  }
}
